import { Colors, Container, Icon, Pills } from '@amelio/ui-library';

import type { ThematicMeeting } from '@/types/thematic-meetings';

import { useAmelioTranslations } from '@/hooks';
import { EmployeeIdentity } from '@/pages/ThematicMeetings/components/EmployeeIdentity';

interface SelectedEmployeeCardProps {
    employee: ThematicMeeting['employee'];
    /** Positions the card within the intro row's grid (`lg:justify-self-end`, offsets) — the
     * card itself only owns its own look, not where it sits. */
    className?: string;
    dataTestId?: string;
}

/**
 * "You selected" card next to the plan's intro card: the only place on this page that shows the
 * employee's identity. It carries no action of its own.
 */
export const SelectedEmployeeCard = ({
    employee,
    className,
    dataTestId = 'thematic-meeting-selected-employee',
}: SelectedEmployeeCardProps) => {
    const { t } = useAmelioTranslations();

    return (
        <Container
            border={'primary'}
            borderRadius={'xl'}
            background={'translucent'}
            glass={'sm'}
            py={'md'}
            className={['relative px-6 lg:min-w-[365px] lg:px-12', className]
                .filter(Boolean)
                .join(' ')}
            dataTestId={dataTestId}
        >
            <Pills
                variant={'primary'}
                size={'sm'}
                className={'absolute -top-2 right-0 z-10'}
                dataTestId={`${dataTestId}-badge`}
            >
                {t('thematicMeetings.plan.selectedEmployee')}
            </Pills>

            <Container
                p={'md'}
                borderRadius={'2xl'}
                border={'white'}
                className={'bg-white/80'}
            >
                <div className={'flex items-center gap-6'}>
                    <EmployeeIdentity
                        employee={employee}
                        size={'xs'}
                        showJobTitle
                        wrapName
                    />

                    <Icon
                        name={'school-briefcase'}
                        size={'lg'}
                        color={Colors.PURPLE}
                        ariaHidden
                    />
                </div>
            </Container>
        </Container>
    );
};
